// referral.jsx - Cleaned version without phone validation/formatting

import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import Swal from 'sweetalert2';
import { decryptData } from './helpers/encryption';
import './referral.css';

const Referral = () => {
    const [business, setBusiness] = useState(null);
    const [countries, setCountries] = useState([]);
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [receivers, setReceivers] = useState([{ countryCode: '+92', number: '', name: '' }]);
    const [referralHistory, setReferralHistory] = useState([]);
    const [loyaltySummary, setLoyaltySummary] = useState(null);
    const [phoneNumber, setPhoneNumber] = useState('');
    const [businessId, setBusinessId] = useState('');
    const sceneRef = useRef(null);
    const [showGiftAnimation, setShowGiftAnimation] = useState(false);
    const [branchIdFromUrl, setBranchIdFromUrl] = useState(null);

    // Get URL parameters (supports both encrypted and path params)
    const getUrlParams = () => {
        const urlParams = new URLSearchParams(window.location.search);
        const encryptedData = urlParams.get('data');
        
        if (encryptedData) {
            return {
                encryptedData: encryptedData,
                isEncrypted: true
            };
        }
        
        // Fallback to path params for backward compatibility
        const path = window.location.pathname.split('/');
        return {
            phone_number: path[1] || '',
            business_id: path[2] || '',
            isEncrypted: false
        };
    };

    const urlParams = getUrlParams();

    // Show gift animation when component loads
    useEffect(() => {
        if (!loading && business) {
            const timer = setTimeout(() => {
                setShowGiftAnimation(true);
                setTimeout(() => {
                    setShowGiftAnimation(false);
                }, 5000);
            }, 1000);
            return () => clearTimeout(timer);
        }
    }, [loading, business]);

    // Initialize app
    useEffect(() => {
        const initializeApp = async () => {
            try {
                let rawPhoneNumber = '';
                let rawBusinessId = '';
                let rawBranchId = null;

                if (urlParams.isEncrypted) {
                    try {
                        const decrypted = decryptData(decodeURIComponent(urlParams.encryptedData));
                        rawPhoneNumber = decrypted.phone;
                        rawBusinessId = decrypted.businessId;
                        rawBranchId = decrypted.branch_id || null;
                        console.log('📦 Decrypted referral data:', { 
                            phone: rawPhoneNumber, 
                            businessId: rawBusinessId, 
                            branchId: rawBranchId 
                        });
                    } catch (decryptError) {
                        console.error('Decryption failed:', decryptError);
                        Swal.fire({
                            icon: 'error',
                            title: 'Invalid Link',
                            text: 'The referral link is invalid or corrupted.',
                            confirmButtonColor: '#d33'
                        });
                        setLoading(false);
                        return;
                    }
                } else {
                    rawPhoneNumber = urlParams.phone_number;
                    rawBusinessId = urlParams.business_id;
                    const urlParamsObj = new URLSearchParams(window.location.search);
                    rawBranchId = urlParamsObj.get('branch_id');
                }

                if (!rawPhoneNumber || !rawBusinessId) {
                    Swal.fire({
                        icon: 'error',
                        title: 'Invalid Link',
                        text: 'Missing required parameters',
                        confirmButtonColor: '#d33'
                    });
                    setLoading(false);
                    return;
                }

                // Store branch_id in state
                setBranchIdFromUrl(rawBranchId);

                const apiBaseUrl = process.env.REACT_APP_RECEIPT_BACK_END_URL || process.env.REACT_APP_RECEIPT_URL;
                
                const countriesRes = await axios.get(`${apiBaseUrl}/api/countries`);
                const countriesData = countriesRes.data;
                setCountries(countriesData);
                
                if (countriesData.length > 0) {
                    setReceivers([{ countryCode: countriesData[0].country_code, number: '', name: '' }]);
                }

                // ✅ Use the phone number AS IS from decrypted data - NO validation, NO formatting
                setPhoneNumber(rawPhoneNumber);
                setBusinessId(rawBusinessId);
                
                await fetchBusinessDetails(rawBusinessId, rawPhoneNumber);
                
            } catch (err) {
                console.error('Failed to initialize app:', err);
                Swal.fire({
                    icon: 'error',
                    title: 'Error',
                    text: 'Failed to load application data',
                    confirmButtonColor: '#d33'
                });
                setLoading(false);
            }
        };

        if (!loading || (urlParams.encryptedData || urlParams.phone_number)) {
            initializeApp();
        }
    }, []);

    const fetchBusinessDetails = async (bizId, phone) => {
        try {
            const apiBaseUrl = process.env.REACT_APP_RECEIPT_BACK_END_URL || process.env.REACT_APP_RECEIPT_URL;
            const res = await axios.get(`${apiBaseUrl}/api/business/${bizId}`);
            setBusiness(res.data);
            await fetchReferralHistory(bizId, phone);
        } catch (err) {
            const errorMsg = err.response?.data?.error || 'Business not found';
            Swal.fire({
                icon: 'error',
                title: 'Error',
                text: errorMsg,
                confirmButtonColor: '#d33'
            });
        } finally {
            setLoading(false);
        }
    };

    const fetchReferralHistory = async (bizId, phone) => {
        try {
            const apiBaseUrl = process.env.REACT_APP_RECEIPT_BACK_END_URL || process.env.REACT_APP_RECEIPT_URL;
            const [historyRes, loyaltyRes] = await Promise.all([
                axios.get(`${apiBaseUrl}/api/referral/history`, {
                    params: { business_id: bizId, phone_number: phone }
                }),
                axios.get(`${apiBaseUrl}/api/loyalty/summary`, {
                    params: { business_id: bizId, phone_number: phone }
                })
            ]);
            
            setReferralHistory(historyRes.data);
            setLoyaltySummary(loyaltyRes.data);
        } catch (err) {
            console.error('Failed to fetch data:', err);
        }
    };

    // Format phone number for input (remove leading zero only)
    const formatPhoneNumber = (number) => {
        let cleaned = number.replace(/\D/g, '');
        if (cleaned.startsWith('0')) {
            cleaned = cleaned.substring(1);
        }
        return cleaned;
    };

    const addReceiver = () => {
        const defaultCountry = countries.length > 0 ? countries[0].country_code : '+92';
        setReceivers([...receivers, { countryCode: defaultCountry, number: '', name: '' }]);
    };

    const updateReceiver = (index, field, value) => {
        const newReceivers = [...receivers];
        
        if (field === 'number') {
            value = formatPhoneNumber(value);
        }
        
        newReceivers[index][field] = value;
        setReceivers(newReceivers);
    };

   const handleAddRow = async (index) => {
        const receiver = receivers[index];
        
        if (!receiver.name.trim() || !receiver.number.trim()) {
            Swal.fire({
                icon: 'warning',
                title: 'Incomplete Information',
                text: 'Please enter name and phone number',
                confirmButtonColor: business?.primary_color || '#3085d6'
            });
            return;
        }

        // Build receiver number with country code
        const receiverNumber = `${receiver.countryCode}${formatPhoneNumber(receiver.number)}`;
        
        // ✅ FIX: Clean the sender phone number - remove any + and fix duplicate 92
        const cleanPhoneNumber = (num) => {
            // Remove all non-digits
            let digits = num.replace(/\D/g, '');
            // Remove leading zeros
            digits = digits.replace(/^0+/, '');
            // Fix duplicate 92 (if has 9292)
            if (digits.startsWith('9292')) {
                digits = digits.substring(2);
            }
            // Ensure single 92 prefix
            if (!digits.startsWith('92')) {
                digits = '92' + digits;
            }
            return digits;
        };
        
        const cleanSender = cleanPhoneNumber(phoneNumber);
        const cleanReceiver = cleanPhoneNumber(receiverNumber);
        
        console.log('📞 Cleaned numbers:', { 
            originalSender: phoneNumber, 
            cleanSender, 
            originalReceiver: receiverNumber, 
            cleanReceiver 
        });
        
        if (cleanReceiver === cleanSender) {
            Swal.fire({
                icon: 'error',
                title: 'Self Referral Not Allowed',
                html: `You cannot refer your own phone number.<br>Please use a different number.`,
                confirmButtonColor: '#d33'
            });
            return;
        }

        setSubmitting(true);

        try {
            const apiBaseUrl = process.env.REACT_APP_RECEIPT_BACK_END_URL || process.env.REACT_APP_RECEIPT_URL;
            
            let branchId = branchIdFromUrl;
            
            console.log('📨 Sending referral:', { 
                business_id: businessId, 
                phone_number: cleanSender,  // ✅ Send cleaned number (e.g., "923033940595")
                receivers: [receiverNumber],
                receiver_names: [receiver.name],
                branch_id: branchId 
            });
            
            const referralResponse = await axios.post(`${apiBaseUrl}/api/referral`, {
                business_id: businessId,
                phone_number: cleanSender,  // ✅ Send without any +
                receivers: [receiverNumber],
                receiver_names: [receiver.name],
                branch_id: branchId ? parseInt(branchId) : null
            });

            let successMessage = `Successfully added ${receiver.name}!`;
            
            if (referralResponse.data.promoCodes && referralResponse.data.promoCodes.length > 0) {
                const promoCode = referralResponse.data.promoCodes[0].promo_code;
                successMessage += `\n\nPromo Code: ${promoCode}`;
            }

            Swal.fire({
                icon: 'success',
                title: 'Friend Added!',
                html: successMessage.replace(/\n/g, '<br>'),
                confirmButtonColor: business?.primary_color || '#3085d6',
            });

            // Clear the input field after successful addition
            const newReceivers = [...receivers];
            newReceivers[index] = { countryCode: receiver.countryCode, number: '', name: '' };
            setReceivers(newReceivers);
            
            await fetchReferralHistory(businessId, phoneNumber);
            
        } catch (err) {
            console.error('❌ Add row error:', err);
            
            let errorMsg = err.response?.data?.error || 'Failed to add friend';
            
            if (err.response?.status === 409) {
                errorMsg = 'This friend has already been referred by you!';
            }

            Swal.fire({
                icon: 'error',
                title: 'Error',
                text: errorMsg,
                confirmButtonColor: '#d33'
            });
        } finally {
            setSubmitting(false);
        }
    };

    // Loading state
    if (loading) {
        return (
            <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '100vh', background: '#f5f5f5' }}>
                <div className="text-center">
                    <div className="spinner-border text-primary" role="status" style={{ width: '3rem', height: '3rem' }}>
                        <span className="visually-hidden">Loading...</span>
                    </div>
                    <p className="mt-3 text-muted">Loading referral page...</p>
                </div>
            </div>
        );
    }

    if (!business) {
        return (
            <div className="error-container">
                <h2>Business Not Found</h2>
                <p>The business you're looking for doesn't exist or is inactive.</p>
            </div>
        );
    }

    const businessColors = {
        primary: business.primary_color || '#6366f1',
        primaryHighlight: business.primary_highlight_color || '#4f46e5',
        secondary: business.secondary_color || '#f59e0b',
        secondaryHighlight: business.secondary_highlight_color || '#d97706'
    };

    return (
        <>
            <style>
                {`
                    * {
                        margin: 0;
                        padding: 0;
                        box-sizing: border-box;
                    }

                    html, body, #root {
                        height: 100%;
                        margin: 0;
                        padding: 0;
                        overflow-x: auto;
                        background: transparent;
                        font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
                    }

                    .main-logo {
                        max-width: 280px;
                        max-height: 280px;
                        border-radius: 20px;
                    }
                    .input-group {
                        flex-wrap: wrap;
                        gap: 10px;
                    }
                `}
            </style>
            
            <div className="referral-container" style={{ 
                '--primary': businessColors.primary, 
                '--primary-highlight': businessColors.primaryHighlight, 
                '--secondary': businessColors.secondary, 
                '--secondary-highlight': businessColors.secondaryHighlight 
            }}>
                
                {/* Centered Content Container */}
                <div className="centered-content">
                    {/* Logo Section */}
                    {business.logo && (
                        <div className="logo-section">
                            <img 
                                src={`${process.env.REACT_APP_RECEIPT_LOGO_URL || ''}/${business.logo}`}
                                alt={business.business_name}
                                className="main-logo"
                            />
                        </div>
                    )}
                    
                    {/* Big Main Heading */}
                    <div className="main-heading-section">
                        <h1 className="big-main-heading">HEY! GET AMAZING DISCOUNTS</h1>
                        <div className="heading-underline"></div>
                    </div>

                    {/* Mid Heading */}
                    <div className="mid-heading-section">
                        <h2 className="mid-heading">Refer Friends & Get Amazing Discounts 🎉</h2>
                    </div>

                    {/* Add Friends Form */}
                    <div className="form-section">
                        <div className="form-container">
                            <div className="friends-input-section">
                                <div className="input-rows-container">
                                    {Array.isArray(receivers) && receivers.map((receiver, index) => (
                                        <div key={index} className="input-row">
                                            <div className="input-group" style={{ display: "flex", gap: "12px", alignItems: "center", flexWrap: "wrap" }}>
                                                {/* Friend Name Input */}
                                                <div className="name-input">
                                                    <input
                                                        type="text"
                                                        value={receiver.name}
                                                        onChange={(e) => updateReceiver(index, 'name', e.target.value)}
                                                        placeholder="Friend's full name"
                                                        className="name-input-field"
                                                        required
                                                    />
                                                </div>
                                                
                                                {/* Phone Number with Integrated Country Code */}
                                                <div className="phone-input-wrapper">
                                                    <div className="phone-input-with-country">
                                                        <div className="country-code-selector">
                                                            <select
                                                                value={receiver.countryCode}
                                                                onChange={(e) => updateReceiver(index, 'countryCode', e.target.value)}
                                                                className="country-code-select"
                                                            >
                                                                {Array.isArray(countries) && countries.map((country) => (
                                                                    <option key={country.country_code} value={country.country_code}>
                                                                        {country.country_code}
                                                                    </option>
                                                                ))}
                                                            </select>
                                                            <div className="country-arrow">▼</div>
                                                        </div>
                                                        <input
                                                            type="tel"
                                                            value={receiver.number}
                                                            onChange={(e) => updateReceiver(index, 'number', e.target.value)}
                                                            placeholder="Phone number"
                                                            className="phone-number-input"
                                                            required
                                                            pattern="[1-9][0-9]*"
                                                            title="Please enter phone number without leading zero"
                                                        />
                                                    </div>
                                                </div>

                                                {/* Add Row Button */}
                                                <div className="action-buttons">
                                                    <button
                                                        type="button"
                                                        onClick={() => handleAddRow(index)}
                                                        disabled={submitting}
                                                        className="add-button"
                                                        title="Add this friend"
                                                    >
                                                        {submitting ? (
                                                            <div className="button-spinner"></div>
                                                        ) : (
                                                            <>
                                                                <span className="button-icon"></span>
                                                                Add Friend
                                                            </>
                                                        )}
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Referral Cards Section */}
                    <div className="referral-cards-section">
                        <div className="referral-cards-container">
                            <div className="section-header">
                                <h3>Your Referred Friends</h3>
                                <p>Track your referrals and earned points</p>
                            </div>

                            <div className="referral-cards-grid">
                                {referralHistory.length > 0 ? (
                                    referralHistory.map((referral, index) => (
                                        <div key={index} className={`referral-card ${referral.status === 'used' ? 'used-referral' : ''}`}>
                                            <div className="card-content">
                                                <div className="friend-section">
                                                    <span className="friend-name-value">
                                                        {referral.receiver_name || `Friend ${index + 1}`}
                                                    </span>
                                                    <span className="friend-number-value">
                                                        {referral.ref_receiver}
                                                    </span>
                                                </div>
                                                
                                                <div className="points-section">
                                                    {referral.points_to_earn > 0 && (
                                                        <div className="points-to-earn">
                                                            <span className="points-label">Points to Earn:</span>
                                                            <span className="points-value">+{referral.points_to_earn}</span>
                                                        </div>
                                                    )}
                                                    {referral.earned_points > 0 && (
                                                        <div className="earned-points">
                                                            <span className="points-label">Earned Points:</span>
                                                            <span className="points-value">+{referral.earned_points}</span>
                                                        </div>
                                                    )}
                                                    <span className={`status-badge ${referral.status}`}>
                                                        {referral.status === 'used' ? 'Promo Used' : 'Pending'}
                                                    </span>
                                                </div>
                                            </div>
                                        </div>
                                    ))
                                ) : (
                                    <div className="no-referrals-card">
                                        <div className="no-data-icon">👥</div>
                                        <h3>No Friends Referred Yet</h3>
                                        <p>Start adding friends to see them here!</p>
                                    </div>
                                )}
                            </div>

                            {/* Loyalty Points Summary */}
                            {loyaltySummary && (
                                <div className="loyalty-summary-cards">
                                    <div className="loyalty-card total-earned">
                                        <span className="loyalty-label">Earned Referral Points:</span>
                                        <span className="loyalty-value">{loyaltySummary.total_earned_loyalty || 0} pts</span>
                                    </div>
                                    <div className="loyalty-card total-used">
                                        <span className="loyalty-label">Used Referral Points:</span>
                                        <span className="loyalty-value">{loyaltySummary.total_used_loyalty || 0} pts</span>
                                    </div>
                                    <div className="loyalty-card available-points">
                                        <span className="loyalty-label">Available Referral Points:</span>
                                        <span className="loyalty-value highlight">{loyaltySummary.available_loyalty || 0} pts</span>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                    
                    {/* Footer */}
                    <div className="referral-footer">
                        <p>Share the love and earn amazing discounts! 💫</p>
                    </div>
                </div>
            </div>
        </>
    );
};

export default Referral;